import { get, post } from '@/lib/api';

/**
 * Acesso às aprovações da Fase 3.
 *
 * ★ **Não há segundo motor de aprovação neste sistema.** O CAB de uma mudança
 * normal, o ECAB de uma emergencial e a exceção de congelamento passam todos
 * por `ApprovalRequest` — mesma trilha, mesma regra de quórum, mesma proibição
 * de autoaprovação. É por isso que este módulo mora em `features/workflow` e
 * não em `features/changes`: quem o usa é a tela de mudança, mas o dono do
 * assunto é o motor.
 *
 * Uma função por rota de `ApprovalsController`, sem nenhuma inventada. O que
 * falta está anotado:
 *
 *   • `GET /approvals` aceita `mine` e `status`, mas **não** `entityId` — o
 *     serviço filtra por ele, o controller não o expõe. Quem quer as aprovações
 *     de uma entidade usa `GET /tickets/:id/approvals` (só `entityType`
 *     `Ticket`) ou vai direto no `GET /approvals/:id`, com o identificador que
 *     a própria entidade guarda. É o caso da exceção de congelamento, cujo
 *     `approvalRequestId` vem na ficha da mudança.
 */

export type SituacaoDeAprovacao =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'EXPIRED';

export const ROTULO_DE_APROVACAO: Record<SituacaoDeAprovacao, string> = {
  PENDING: 'Aguardando decisão',
  APPROVED: 'Aprovada',
  REJECTED: 'Reprovada',
  CANCELLED: 'Cancelada',
  EXPIRED: 'Vencida',
};

export type DesfechoDeEtapa = 'APPROVED' | 'REJECTED' | 'PENDING' | 'SKIPPED';

export const ROTULO_DE_ETAPA: Record<DesfechoDeEtapa, string> = {
  APPROVED: 'aprovada',
  REJECTED: 'reprovada',
  PENDING: 'pendente',
  SKIPPED: 'dispensada',
};

export interface PassoResolvido {
  key: string;
  stageKey: string;
  /** Congelado na abertura — trocar o gestor amanhã não muda quem decide hoje. */
  resolvedUserId: string;
}

export interface EtapaResolvida {
  key: string;
  order: number;
  rule: { kind: 'ANY' | 'ALL' | 'QUORUM_N' | 'PERCENT'; n?: number; percent?: number };
  steps: PassoResolvido[];
  skipped: boolean;
  skipReason: string | null;
}

export interface DecisaoRegistrada {
  id: string;
  requestId: string;
  stageKey: string;
  stepKey: string;
  decidedById: string;
  decidedAt: string;
  decision: 'APPROVED' | 'REJECTED';
  comment: string | null;
  onBehalfOf: string | null;
  entityVersion: number;
}

/** Quem decide no lugar de quem, dentro de um passo. */
export interface Delegacao {
  stepKey: string;
  fromUserId: string;
  toUserId: string;
}

export interface FotoDaAprovacao {
  status: SituacaoDeAprovacao;
  stages: Array<{ key: string; outcome: DesfechoDeEtapa }>;
  activeStageKeys: string[];
  pendingStepKeys: string[];
}

export interface Aprovacao {
  id: string;
  policyKey: string;
  mode: 'SEQUENTIAL' | 'PARALLEL';
  entityType: string;
  entityId: string;
  entityVersion: number;
  requesterId: string;
  status: SituacaoDeAprovacao;
  forbidSelfApproval: boolean;
  stages: EtapaResolvida[];
  delegations: Delegacao[];
  openedAt: string;
  dueAt: string | null;
  closedAt: string | null;
  closureReason: string | null;
  decisions: DecisaoRegistrada[];
  snapshot: FotoDaAprovacao;
  /** Só em `GET /approvals?mine=true`: os passos que **eu** posso decidir. */
  myPendingStepKeys?: string[];
}

export const listarAprovacoesDoChamado = (ticketId: string) =>
  get<Aprovacao[]>(`/tickets/${ticketId}/approvals`);

export const obterAprovacao = (id: string) => get<Aprovacao>(`/approvals/${id}`);

export const listarMinhasAprovacoes = () =>
  get<Aprovacao[]>('/approvals?mine=true&status=PENDING');

/**
 * Registra a decisão de uma etapa.
 *
 * Quem decide é o usuário do token, nunca um campo do corpo — aceitar
 * `decidedById` faria a proibição de autoaprovação ser contornável escrevendo o
 * id de outra pessoa.
 */
export const decidirAprovacao = (
  id: string,
  corpo: { stepKey: string; decision: 'APPROVED' | 'REJECTED'; comment?: string },
) => post<{ request: Aprovacao; snapshot: FotoDaAprovacao }>(`/approvals/${id}/decisions`, corpo);

export const chavesDeAprovacao = {
  raiz: ['aprovacoes'] as const,
  minhas: ['aprovacoes', 'minhas'] as const,
  aprovacao: (id: string) => ['aprovacoes', 'item', id] as const,
  doChamado: (ticketId: string) => ['chamados', 'item', ticketId, 'aprovacoes'] as const,
};
